import React, { ReactNode, useState } from 'react';
import { Form, Button, Alert, Card, Modal } from 'react-bootstrap';
import { useForm, usePage, Link } from '@inertiajs/react';
import { router } from '@inertiajs/react';

import Valuation from '../../Data/Valuation';
import AuthedLayout from '../../Layouts/Authed';
import FormHeader from '../../Components/Forms/FormHeader';

interface Props {
	valuation: Valuation;
	allStepsComplete: boolean;
	missingSteps: string[];
	previousPage: string;
}

const Finalize = (props: Props) => {
	const { flash } = usePage().props as any;
	const { valuation, allStepsComplete, missingSteps } = props;
	const url = `/valuation-form/${valuation.id}/finalize`;

	const [showCompleteModal, setShowCompleteModal] = useState(false);
	const [showReopenModal, setShowReopenModal] = useState(false);
	const [completing, setCompleting] = useState(false);
	const [reopening, setReopening] = useState(false);
	const [deleting, setDeleting] = useState(false);

	const { data, setData, post, processing, progress, errors } = useForm({
		report_file: null as File | null,
	});

	const handleUpload = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		post(`${url}/upload`);
	};

	const handleDelete = () => {
		setDeleting(true);
		router.delete(`${url}/report`, {
			onFinish: () => setDeleting(false),
		});
	};

	const handleMarkComplete = () => {
		setCompleting(true);
		router.post(`${url}/complete`, {}, {
			onFinish: () => {
				setCompleting(false);
				setShowCompleteModal(false);
			},
		});
	};

	const handleReopen = () => {
		setReopening(true);
		router.post(`/valuation/${valuation.id}/reopen`, {}, {
			onFinish: () => {
				setReopening(false);
				setShowReopenModal(false);
			},
		});
	};

	const isComplete = valuation.status === 'Complete';

	return (
		<div>
			<FormHeader
				agencyName={valuation.agency_name}
				effectiveDate={valuation.effective_date}
				pageName="Finalize Valuation"
			/>

			{flash?.success && (
				<Alert variant="success" className="mb-4">
					{flash.success}
				</Alert>
			)}

			{!allStepsComplete && !isComplete && (
				<Alert variant="warning" className="mb-4">
					All form sections must be completed before uploading a report.
					Incomplete sections: {missingSteps.join(', ')}.
				</Alert>
			)}

			{isComplete && (
				<Alert variant="info" className="mb-4">
					This valuation has been marked as complete. The report is available for the client to download.
				</Alert>
			)}

			<Card className="mb-4">
				<Card.Header>Valuation Report</Card.Header>
				<Card.Body>
					{valuation.has_report ? (
						<div className="d-flex align-items-center gap-2">
							<span>{valuation.report_filename ?? 'Report'}</span>
							<a
								href={`/valuation/${valuation.id}/download-report`}
								target="_blank"
								rel="noopener noreferrer"
								className="btn btn-sm btn-outline-primary"
							>
								View
							</a>
							{!isComplete && (
								<Button
									variant="outline-danger"
									size="sm"
									onClick={handleDelete}
									disabled={deleting}
								>
									{deleting ? 'Removing…' : 'Remove Report'}
								</Button>
							)}
						</div>
					) : (
						<div>
							{!isComplete && allStepsComplete ? (
								<Form onSubmit={handleUpload}>
									<Form.Group className="mb-3">
										<Form.Label>Upload Report PDF</Form.Label>
										<Form.Control
											type="file"
											accept=".pdf"
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setData('report_file', e.target.files?.[0] ?? null)
											}
											isInvalid={!!errors.report_file}
										/>
										<Form.Control.Feedback type="invalid">
											{errors.report_file}
										</Form.Control.Feedback>
										<Form.Text className="text-muted">
											PDF only, maximum 20 MB.
										</Form.Text>
									</Form.Group>
									<Button type="submit" disabled={!data.report_file || processing}>
										{processing ? 'Uploading…' : 'Upload Report'}
									</Button>
								</Form>
							) : (
								<p className="text-muted mb-0">
									No report has been uploaded yet.
								</p>
							)}
						</div>
					)}
				</Card.Body>
			</Card>

			{!isComplete && allStepsComplete && (
				<Card>
					<Card.Header>Mark as Complete</Card.Header>
					<Card.Body>
						<p className="text-muted mb-3">
							Once marked as complete, the valuation form will be locked and the report
							will be available for the client to download from their dashboard.
						</p>
						<Button
							variant="primary"
							onClick={() => setShowCompleteModal(true)}
							disabled={!valuation.has_report}
						>
							Mark as Complete
						</Button>
						{!valuation.has_report && (
							<Form.Text className="text-muted ms-2">
								Upload a report first.
							</Form.Text>
						)}
					</Card.Body>
				</Card>
			)}

			{isComplete && (
				<Card className="mb-4">
					<Card.Header>Reopen Valuation</Card.Header>
					<Card.Body>
						<p className="text-muted mb-3">
							Reopening will change the status back to "Under Review" and allow
							the form and report to be edited.
						</p>
						<Button
							variant="primary"
							onClick={() => setShowReopenModal(true)}
						>
							Reopen Valuation
						</Button>
					</Card.Body>
				</Card>
			)}

			<div className='d-flex justify-content-start'>
				<Button
					as={Link as any}
					href={props.previousPage}
					variant='outline-primary'
					className='mt-4'
				>
					Back
				</Button>
			</div>

			<Modal show={showReopenModal} onHide={() => setShowReopenModal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>Reopen Valuation</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					Are you sure you want to reopen this valuation? The status will change
					to "Under Review" and the form will be editable again.
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={() => setShowReopenModal(false)}>
						Cancel
					</Button>
					<Button variant="primary" onClick={handleReopen} disabled={reopening}>
						{reopening ? 'Reopening…' : 'Yes, Reopen'}
					</Button>
				</Modal.Footer>
			</Modal>

			<Modal show={showCompleteModal} onHide={() => setShowCompleteModal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>Mark Valuation as Complete</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					Are you sure you want to mark this valuation as complete? The form will
					be locked and the report will become available for download by the client.
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={() => setShowCompleteModal(false)}>
						Cancel
					</Button>
					<Button variant="primary" onClick={handleMarkComplete} disabled={completing}>
						{completing ? 'Completing…' : 'Yes, Mark as Complete'}
					</Button>
				</Modal.Footer>
			</Modal>
		</div>
	);
};

Finalize.layout = (page: ReactNode) => <AuthedLayout>{page}</AuthedLayout>;

export default Finalize;
